import { Flex, Skeleton } from "antd"
import { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"

import { FixedContainer } from "../../../../components/FixedContainer.tsx"
import { RecordNotFound } from "../../../../components/RecordNotFound/RecordNotFound.tsx"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  getProjectConversations,
  roomsOf
} from "../../../Messages/api/services.ts"
import { upsertConversation } from "../../../Messages/redux/messaging.slice.ts"
import { ProjectPageNavigation } from "../../../../components/ProjectPageNavigation.tsx"
import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  selectActiveBranchId,
  selectCompanyIsMultiBranch
} from "../../../Branches/redux/branches.slice.ts"
import type { RootState } from "../../../../store.ts"
import { getInvoiceSnapshotVisibility } from "../../helpers/get-invoice-snapshot-visibility.tsx"
import { canViewFinanceSnapshot } from "../../helpers/project-finance-visibility.ts"
import {
  canChangeProjectStatus,
  canCloseProject
} from "../../helpers/project-edit-visibility.ts"
import { ProjectNotLoaded } from "../../components/ProjectNotLoaded.tsx"
import { fetchProject } from "../../redux/project.thunks.ts"
import { ProjectViewHeader } from "./ProjectViewHeader.tsx"
import {
  type ProjectSectionKeys,
  ProjectViewSectionsTabs,
  projectViewTabs
} from "./ProjectViewSectionsTabs.tsx"
import { ProjectViewSidebar } from "./ProjectViewSidebar.tsx"
import { ProjectArchiveBanner } from "./ProjectArchiveBanner.tsx"

function ProjectViewLayout({
  userRole,
  project,
  fetching,
  loadError,
  permissions,
  getProjectById
}: ConnectedProps<typeof connector>) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const projectPermissions = permissions?.codes?.projects
  const projectFeatured = projectPermissions?.features

  // The backend's detail retrieve is deliberately tenant-wide, not branch-
  // scoped (apps.project_management.views.ProjectViewSet.get_queryset only
  // narrows "list"/"export" by the active branch) — an authorized owner
  // opening any of their company's projects by id is correct by design.
  // What was missing: nothing reacted when the WORKSPACE itself moved away
  // from this project's own branch while its detail page stayed open —
  // the page just kept rendering whatever it had already loaded.
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const isBranchWorkspace = isMultiBranch && !!activeBranchId

  const showSalesSnapshot = !!projectFeatured?.project_snapshot_sales
  const showCommissionSnapshot = !!projectFeatured?.project_snapshot_commissions
  const showFinanceSnapshot = canViewFinanceSnapshot(projectPermissions)
  const showMilestoneSnapshot =
    !!projectPermissions?.milestones?.project_milestone_view

  const showInvoiceSnapshot = useMemo(
    () => getInvoiceSnapshotVisibility({ userRole, projectPermissions }),
    [userRole]
  )

  const hasSnapShot =
    showFinanceSnapshot || showInvoiceSnapshot || showMilestoneSnapshot

  const activeTab =
    projectViewTabs.find((option) => location.pathname.includes(option.value))
      ?.value || null

  // The Messages tab's own badge. Loaded here rather than by the tab itself,
  // because a badge that only appears once you have already opened Messages
  // is not a badge. Reads from the store afterwards, so it falls as the
  // conversations are read without this layout refetching.
  const dispatch = useAppDispatch()
  const messagesUnread = useAppSelector((state) =>
    state.messaging.conversations
      .filter((conversation) => conversation.project === id)
      .reduce((total, conversation) => total + conversation.unread_count, 0)
  )

  useEffect(() => {
    if (!id) return
    let cancelled = false
    const loadUnread = async () => {
      const response = await getProjectConversations(id)
      if (cancelled || !response.ok) return
      roomsOf(response.data).forEach((room) =>
        dispatch(upsertConversation(room))
      )
    }
    loadUnread()
    return () => {
      cancelled = true
    }
  }, [id, dispatch])

  useEffect(() => {
    if (id) {
      getProjectById(id)
    }

    generateDocumentTitle("View Project")
  }, [id])

  if (fetching) return <Skeleton.Node active className={"size-full"} />

  if (!project) {
    return (
      <ProjectNotLoaded
        id={id as string}
        loadError={loadError}
        onRetry={() => getProjectById(id as string)}
      />
    )
  }

  // The workspace switched to a specific branch that isn't this project's
  // own — the owner is still authorized to open it (hence `project` loaded
  // successfully), but drilling into it from the wrong branch workspace
  // would read every surface against a record that silently belongs
  // elsewhere. Company HQ ("All branches") is deliberately exempt: an
  // owner there is meant to reach any of their projects.
  if (
    isBranchWorkspace &&
    project.team_id &&
    project.team_id !== activeBranchId
  ) {
    return (
      <RecordNotFound
        titleKey={"recordNotFound.project.differentBranchTitle"}
        hintKey={"recordNotFound.project.differentBranchHint"}
        backTo={RoutesPaths.project.list}
        backLabelKey={"recordNotFound.project.back"}
      />
    )
  }

  const handleTabChange = (tab: ProjectSectionKeys) => {
    if (!id) return

    const variants: Partial<Record<ProjectSectionKeys, string>> = {
      milestones: RoutesPaths.project.view(id).milestones,
      finance: RoutesPaths.project.view(id).finance,
      overview: RoutesPaths.project.view(id).overviewTab,
      invoices: RoutesPaths.project.view(id).invoices,
      files: RoutesPaths.project.view(id).files(),
      tasks: RoutesPaths.project.view(id).tasks,
      commissions: RoutesPaths.project.view(id).commissions,
      messages: RoutesPaths.project.view(id).messages(),
      logs: RoutesPaths.project.view(id).logs
    }

    const href = variants[tab]
    if (!href) return

    navigate(href)
  }

  return (
    <FixedContainer flushBottom>
      <ProjectPageNavigation permissions={permissions?.codes?.projects} />
      {/* The header and the section tabs span the page, above the content and
          the snapshot panel beside it. Squeezed into the content column next
          to the 565px panel, the tabs fell back to unlabelled icons and the
          project name was cut off on any laptop-sized screen. */}
      <Flex vertical gap={"small"} flex={1} className={"min-h-0 min-w-0"}>
        <ProjectArchiveBanner
          project={project}
          canRestore={!!projectFeatured?.project_archive}
        />
        <div data-guide-target="project-header-panel" className="contents">
          <ProjectViewHeader
            canChangeStatus={canChangeProjectStatus(
              projectPermissions,
              project
            )}
            canClose={canCloseProject(projectPermissions, project)}
            canViewLogs={!!projectPermissions?.features?.project_activity_log}
            activeTab={activeTab}
            project={project}
            showSnapshotToggle={hasSnapShot}
            showFinanceSnapshot={showFinanceSnapshot}
          />
        </div>

        <ProjectViewSectionsTabs
          badges={{ messages: messagesUnread }}
          permissions={projectPermissions}
          activeTab={activeTab}
          onSectionChange={handleTabChange}
        />

        <Flex
          flex={1}
          className={"min-h-0 min-w-0"}
          data-testid={"project-content-row"}
        >
          <Flex vertical gap={"small"} flex={1} className={"min-h-0 min-w-0"}>
            <Outlet context={{ project }} />
          </Flex>

          {hasSnapShot && (
            <ProjectViewSidebar
              activeTab={activeTab}
              showSalesSnapshot={showSalesSnapshot}
              showCommissionSnapshot={showCommissionSnapshot}
              showInvoiceSnapshot={showInvoiceSnapshot}
              showMilestoneSnapshot={showMilestoneSnapshot}
            />
          )}
        </Flex>
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  userRole: state?.auth?.userInfo?.role?.id,
  fetching: state.project.fetching,
  loadError: state.project.loadError,
  project: state.project.project,
  permissions: state.auth.permissions
})

const mapDispatchToProps = {
  getProjectById: fetchProject
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewLayout)
